import { Inject, Injectable } from "@angular/core";
import { AirportEntity, AppMetaData, CheckRideRequestCalendarQueryEntity, CheckRideRequestEntity, CheckrideRequestSchoolCalendarQueryEntity, CheckRideRequestSubStatusAllItems, FlightSchoolApplicantEntity, FlightSchoolApplicantStatusAllItems, FlightSchoolOrganizationEntity } from "../../../meta-data/app-meta-data.service";
import { DwOrmDataServiceToken, DwOrmDataService, DwMetaDataServiceToken, DwMetaDataService, DwConfigServiceToken, DwConfigService, DwSecurityUserService, DwCacheService, DwQueryFilter, removeNullProperties, deepCopy } from "@devwareapps/devware-cap";
import { Observable, of } from "rxjs";
import { AdminManagerService } from "../../shared/services/admin-manager.service";
import { mergeMap, tap } from "rxjs/operators";
import { AirportMetar } from "../../examiner/models/airport-metar.model";
import { MetarRepositoryService } from "../../shared/services/metar-repository-service";
import { ValidateSchoolCheckridesResult } from "../models/validate-school-checkrides-result.model";
import { HttpClient } from "@angular/common/http";
import { ValidateSchoolCheckridesRequest } from "../models/validate-school-checkrides-request.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";


@Injectable({
    providedIn: 'root'
})
export class SchoolCheckrideUtilService {
    static flightSchoolOrganization: FlightSchoolOrganizationEntity;


    apiRoot: string;
    constructor(
        @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private adminManagerSerice: AdminManagerService,
        private metarRepositoryService: MetarRepositoryService,
        private http: HttpClient,
        private dateTimeUtilService: DateTimeUtilService,
    ) {
        this.apiRoot = configService.coreConfig.apiRoot;
    }

    updateCheckrideApplicantData(checkride: CheckRideRequestEntity, applicant: FlightSchoolApplicantEntity, removeNullPropertiesOption: boolean = true): Partial<CheckRideRequestEntity> {
        let checkrideUpdate: Partial<CheckRideRequestEntity> = {
            // ——— 1) DIRECT / SAFE COPIES (same meaning, clear intent) ———
            FlightSchoolApplicantId: applicant.FlightSchoolApplicantId,
            StudentApplicantId: applicant.StudentApplicantId,
            FlightSchoolOrganizationId: applicant.FlightSchoolOrganizationId,
            CheckRideGroupId: applicant.CheckRideGroupId,
            CheckRideTypeId: applicant.CheckRideTypeId,
            CheckRideTestTypeId: applicant.CheckRideTestTypeId,
            FlightSchoolBranchId: applicant.FlightSchoolBranchId,
            FlightSchoolInstructorId : applicant.PrimaryFlightSchoolInstructorId,
            EndorsedAndCheckRideReady: applicant.IsEndorsed,
            EndorsedDate : applicant.EndorsedDate,
            AuthorityPreCheckRideNumber: applicant.AuthorityPreCheckRideNumber,
            PilotCertificateNumber: applicant.PilotCertificateNumber,
            CurrentPilotCertifiateTypeId: applicant.CurrentPilotCertificateTypeId, // NOTE: property name in target is misspelled
            StudentApplicantStateProvinceId: applicant.StateProvinceId,
            ForeignPilotMeetsQualifications: applicant.ForeignPilotMeetsQualifications,
            AuthorityApplicationNumber: applicant.AuthorityApplicationNumber,
            //ApplicantAcsWrittenResultId: applicant.ApplicantAcsWrittenResultId,
            
            AirmanKnowledgeTestScore: applicant.AirmanKnowledgeTestScore,
            AirmanKnowledgeTestDate: applicant.AirmanKnowledgeTestDate,
            AirmanKnowledgeTestPDFMediaId: applicant.AirmanKnowledgeTestPDFMediaId,
            // Typos normalized:
            //  - source AirplaneMakeModal -> target AirplaneMakeModel
            AirplaneMakeModel: applicant.AirplaneMakeModal,
            // Citizenship: CountryId -> StudentApplicantCitizenshipCountyId (target prop is spelled "County")
            StudentApplicantCitizenshipCountyId: applicant.CitizenshipCountryId,

            // PreferredDateTime is a safer default for an *expected* date
            //PreferredDateTime: applicant.ExpectedCheckrideDateTime,
            AdditionalDocumentationNotes: applicant.AdditionalDocumentationNotes,
            LogBookMediaId: applicant.LogBookMediaId,
            TrainingMediaId: applicant.TrainingMediaId,
            DocumentsMediaGroupId: applicant.AdditionalDocumentsMediaGroupId,
            StudentEndOfCourseDate: applicant.EndOfCourseDate,
            FlightSchoolPrivateNotes: applicant.FlightSchoolPrivateNotes,
            AdditionalAirmanKnowledgeTestScore: applicant.AdditionalAirmanKnowledgeTestScore,
            AdditionalAirmanKnowledgeTestDate: applicant.AdditionalAirmanKnowledgeTestDate,
            AdditionalAirmanKnowledgeTestPDFMediaId: applicant.AdditionalAirmanKnowledgeTestPDFMediaId,
            OdaFlightSchoolId : applicant.ODAFlightSchoolId,
            CheckRideInstructorTypeId : applicant.CheckRideInstructorTypeId,
            FlightInstructorName : applicant.FlightInstructorName,
            FlightInstructorEmail : applicant.FlightInstructorEmail,
            FlightInstructorPhone : applicant.FlightInstructorPhone,
            FlightInstructorCertificateNumber : applicant.FlightInstructorCertificateNumber,
            //FlightSchoolTypeId : applicant.FlightSchoolTypeId,
        };

        checkrideUpdate.ApplicantAcsWrittenResult = deepCopy(applicant.ApplicantAcsWrittenResult);
        checkrideUpdate.ApplicantAcsWrittenResultId = applicant.ApplicantAcsWrittenResultId;

        // If the applicant’s expected date is in the past, don’t overwrite an existing scheduled time.
        // Otherwise allow upgrading PreferredDateTime to ScheduledDateTime (if you want to auto-schedule).
        if (applicant.ExpectedCheckrideDateTime) {
            if (!this.dateTimeUtilService.isBeforeToday(applicant.ExpectedCheckrideDateTime)) {
                checkrideUpdate.PreferredDateTime = applicant.ExpectedCheckrideDateTime;
            } else if (checkride?.ScheduledDateTime) {
                // keep existing scheduled value if the applicant's expected date is stale
                checkrideUpdate.PreferredDateTime = checkride.ScheduledDateTime;
            }
        }

        if (removeNullPropertiesOption) {
            checkrideUpdate = removeNullProperties(checkrideUpdate);
        }

        return checkrideUpdate;
    }

    updateCheckrideApplicantDataOld(checkride: CheckRideRequestEntity, currentApplicant: FlightSchoolApplicantEntity): Partial<CheckRideRequestEntity> {
        let checkrideRequestUpdate: Partial<CheckRideRequestEntity> = {
            CheckRideGroupId: currentApplicant.CheckRideGroupId,
            CheckRideTypeId: currentApplicant.CheckRideTypeId,
            CheckRideTestTypeId: currentApplicant.CheckRideTestTypeId,
            StudentApplicantCitizenshipCountyId: currentApplicant.CitizenshipCountryId,
            ScheduledDateTime: currentApplicant.ExpectedCheckrideDateTime,
            FlightSchoolInstructorId: currentApplicant.PrimaryFlightSchoolInstructorId,
            AuthorityPreCheckRideNumber: currentApplicant.AuthorityPreCheckRideNumber,
            EndorsedAndCheckRideReady: currentApplicant.IsEndorsed,
            AirplaneMakeModel: currentApplicant.AirplaneMakeModal,
            PilotCertificateNumber: currentApplicant.PilotCertificateNumber,
            FlightSchoolBranchId: currentApplicant.FlightSchoolBranchId,
            CurrentPilotCertifiateTypeId: currentApplicant.CurrentPilotCertificateTypeId,
        };

        checkrideRequestUpdate.StudentApplicantStateProvinceId = currentApplicant.StateProvinceId;
        checkrideRequestUpdate.ForeignPilotMeetsQualifications = currentApplicant.ForeignPilotMeetsQualifications;
        checkrideRequestUpdate.AuthorityApplicationNumber = currentApplicant.AuthorityApplicationNumber;

        if (this.dateTimeUtilService.isBeforeToday(currentApplicant.ExpectedCheckrideDateTime)) {
            checkrideRequestUpdate.ScheduledDateTime = checkride.ScheduledDateTime;
        }

        if (currentApplicant.FlightSchoolBranch) {
            checkrideRequestUpdate.ScheduledAirportId = currentApplicant.FlightSchoolBranch.MainAirportId;
            checkrideRequestUpdate.PrimaryTrainingAirportId = currentApplicant.FlightSchoolBranch.MainAirportId;
        }

        // Don't update any properties that are null
        checkrideRequestUpdate = removeNullProperties(checkrideRequestUpdate);

        return checkrideRequestUpdate;
    }


    determineCheckrideChanges(newItem: CheckRideRequestEntity, previousItem: CheckRideRequestEntity) {

    }
}

